import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImageIcon, ImageOff, Loader2, Plus, X } from "lucide-react";
import client, { unwrap, apiErrorMessage } from "@/api/client";
import { cn } from "@/lib/utils";

export const MAX_IMAGES = 10;
const MAX_BYTES = 15 * 1024 * 1024; // the upload endpoint's limit
const TYPES = ["image/jpeg", "image/png", "image/webp"];

let tempId = 0;

/**
 * images: [{ key, fileId, url, uploading }]. Each picked photo uploads straight away; the
 * showroom only stores the resulting file ids when it is saved.
 */
export default function ShowroomImages({ images, setImages }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const room = MAX_IMAGES - images.length;

  const addFiles = (fileList) => {
    const picked = [...(fileList || [])];
    const valid = picked.filter((f) => TYPES.includes(f.type) && f.size <= MAX_BYTES);
    if (valid.length < picked.length) toast.error("Only JPG, PNG or WebP images up to 15 MB can be added");
    if (valid.length > room) toast.error(`A showroom can have up to ${MAX_IMAGES} photos`);
    valid.slice(0, Math.max(room, 0)).forEach(async (file) => {
      const key = `new-${++tempId}`;
      setImages((list) => [...list, { key, fileId: null, url: URL.createObjectURL(file), uploading: true }]);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { id } = unwrap(
          await client.post("/files/upload?category=SHOWROOM", formData, { headers: { "Content-Type": "multipart/form-data" } }),
        );
        setImages((list) => list.map((img) => (img.key === key ? { ...img, fileId: id, uploading: false } : img)));
      } catch (err) {
        toast.error(apiErrorMessage(err, `Couldn't upload ${file.name}`));
        setImages((list) => list.filter((img) => img.key !== key));
      }
    });
  };

  return (
    <div className="space-y-3">
      {room > 0 && (
        <div
          role="button"
          tabIndex={0}
          data-testid="showroom-images-dropzone"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), inputRef.current?.click())}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(e.dataTransfer.files);
          }}
          className={cn(
            "flex items-center justify-center gap-4 rounded-xl border-2 border-dashed px-5 py-4 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
            dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
          )}
        >
          <ImageIcon className="h-7 w-7 text-accent shrink-0" strokeWidth={1.5} />
          <div className="text-sm">
            <p className="text-foreground">Drag & drop images here or click to browse</p>
            <p className="text-xs text-muted-foreground">
              Up to {MAX_IMAGES} images (JPG, PNG, WebP) · {images.length ? `${room} more allowed` : "the first is the cover"}
            </p>
          </div>
          <input
            ref={inputRef}
            data-testid="showroom-images-input"
            type="file"
            accept={TYPES.join(",")}
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
          {images.map((img, i) => (
            <div key={img.key} data-testid="showroom-image-thumb" className="relative aspect-[5/4] rounded-lg overflow-hidden bg-secondary">
              {img.url ? (
                <img src={img.url} alt="" className={cn("h-full w-full object-cover", img.uploading && "opacity-50")} />
              ) : (
                <span className="h-full w-full flex items-center justify-center text-muted-foreground"><ImageOff className="h-5 w-5" /></span>
              )}
              {img.uploading && <Loader2 className="absolute inset-0 m-auto h-5 w-5 animate-spin text-foreground" />}
              {i === 0 && !img.uploading && (
                <span className="absolute bottom-1 left-1 rounded bg-card/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground">Cover</span>
              )}
              <button
                type="button"
                aria-label="Remove photo"
                onClick={() => setImages((list) => list.filter((x) => x.key !== img.key))}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-card/95 shadow-sm flex items-center justify-center text-foreground hover:bg-card"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {room > 0 && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="aspect-[5/4] rounded-lg bg-secondary/70 hover:bg-secondary flex flex-col items-center justify-center gap-1 text-xs text-foreground/80"
            >
              <Plus className="h-4 w-4" /> Add More
            </button>
          )}
        </div>
      )}
    </div>
  );
}
